import sacService from "@/services/sac";
import store from "@/store/store";
import TokenHelper from "@/utils/token-helper";
import Vue from "vue";
import Router from "vue-router";

// Eagerly loaded views: the unauthenticated entry points every user hits
// first. Everything else is lazy-loaded per route so the initial bundle
// stays small — important for coaches on slow school Wi-Fi and old laptops.
import Home from "./views/Home.vue";
import Login from "./views/Login.vue";
import SACRedirect from "./views/SACRedirect.vue";

// Lazily loaded views (each becomes its own webpack chunk, fetched on first
// navigation to the route and cached afterwards)
const TournamentRoundPresentation = () =>
  import("./components/admins/tournaments/TournamentRoundPresentation.vue");
const Coaching = () => import("./components/training/coaching/Coaching.vue");
const Curriculum = () => import("./components/training/curriculum/index.vue");
const TopicExplorer = () =>
  import("./components/training/explorer/templates/TopicExplorer.vue");
const Lecture = () => import("./components/training/lectures/Lecture.vue");
const LecturesCalendar = () =>
  import("./components/training/lectures/pages/calendar.vue");
const LecturesIndex = () =>
  import("./components/training/lectures/pages/index.vue");
const WarmupRoundPresentation = () =>
  import("./components/training/lectures/WarmupRoundPresentation.vue");
const OfflineClassTournament = () =>
  import("./components/training/tournaments/OfflineClassTournament.vue");
const OfflineClassTournaments = () =>
  import("./components/training/tournaments/OfflineClassTournaments.vue");
const AdminNews = () => import("./views/Admins/AdminNews.vue");
const AdminNotifications = () =>
  import("./views/Admins/AdminNotifications.vue");
const AdminPermissions = () => import("./views/Admins/AdminPermissions.vue");
const AdminSACLogs = () => import("./views/Admins/AdminSACLogs.vue");
const AdminTickets = () => import("./views/Admins/AdminTickets.vue");
const AdminTournament = () => import("./views/Admins/AdminTournament.vue");
const AdminTournaments = () => import("./views/Admins/AdminTournaments.vue");
const AdminTournamentSimulation = () =>
  import("./views/Admins/AdminTournamentSimulation.vue");
const AivAi = () => import("./views/AivAi");
const EventRegister = () => import("./views/EventRegister.vue");
const Game = () => import("./views/Game.vue");
const GameV2 = () => import("./views/GameV2");
const HallOfFame = () => import("./views/HallOfFame.vue");
const Homework = () => import("./views/Homework.vue");
const Leaderboards = () => import("./views/Leaderboards.vue");
const Lessons = () => import("./views/Lessons.vue");
const LiveLessons = () => import("./views/LiveLessons");
const LiveMatches = () => import("./views/LiveMatches.vue");
const Lobby = () => import("./views/Lobby.vue");
const Profile = () => import("./views/Profile.vue");
const PvAi = () => import("./views/PvAi");
const PvAiLevel = () => import("./views/PvAiLevel");
const PvP = () => import("./views/PvP");
const Ratings = () => import("./views/Ratings");
const Simul = () => import("./views/Simul.vue");
const SimulCreateOrEdit = () => import("./views/SimulCreateOrEdit.vue");
const SimulGames = () => import("./views/SimulGames.vue");
const Simuls = () => import("./views/Simuls.vue");
const StudentClassHistory = () => import("./views/StudentClassHistory.vue");
const StudentProgress = () => import("./views/StudentProgress.vue");
const TeacherHome = () => import("./views/Teachers/TeacherHome.vue");
const TeacherHomework = () => import("./views/Teachers/TeacherHomework.vue");
const TeacherStudentProgress = () =>
  import("./views/Teachers/TeacherStudentProgress.vue");
const TestPage = () => import("./views/TestPage.vue");
const Tournament = () => import("./views/Tournament.vue");
const TournamentLive = () => import("./views/TournamentLive.vue");
const TournamentRound = () => import("./views/TournamentRound.vue");
const Tournaments = () => import("./views/Tournaments.vue");

Vue.use(Router);

const router = new Router({
  routes: [
    {
      path: "/event/register",
      name: "eventRegister",
      component: EventRegister,
      meta: {
        skipAuth: true,
      },
    },
    {
      path: "/login",
      name: "login",
      component: Login,
      meta: {
        skipAuth: true,
      },
    },
    {
      path: "/sac_redirect",
      name: "sacRedirect",
      component: SACRedirect,
      meta: {
        skipAuth: true,
      },
    },
    {
      path: "/",
      name: "home",
      component: Home,
    },
    {
      path: "/profile",
      name: "profile",
      component: Profile,
    },
    {
      path: "/progress",
      name: "studentProgress",
      component: StudentProgress,
    },
    {
      path: "/my-class",
      name: "studentClassHistory",
      component: StudentClassHistory,
    },
    {
      path: "/homework",
      name: "homework",
      component: Homework,
    },
    {
      path: "/lobby",
      name: "lobby",
      component: Lobby,
    },
    {
      path: "/about",
      name: "about",
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import(/* webpackChunkName: "about" */ "./views/About"),
    },
    {
      path: "/games/:gameId",
      name: "game",
      component: Game,
    },
    {
      path: "/games/v2/:gameId",
      name: "gameV2",
      component: GameV2,
    },
    {
      path: "/aivai",
      name: "aiVsAi",
      component: AivAi,
    },
    {
      path: "/pvai",
      name: "pVAi",
      component: PvAi,
    },
    {
      path: "/pvai-level",
      name: "pVAiLevel",
      component: PvAiLevel,
    },
    {
      path: "/pvp",
      name: "pVp",
      component: PvP,
    },
    {
      path: "/live",
      name: "liveMatches",
      component: LiveMatches,
    },
    {
      path: "/simuls",
      name: "simuls",
      component: Simuls,
    },
    {
      path: "/simuls/new",
      name: "simulCreate",
      component: SimulCreateOrEdit,
    },
    {
      path: "/simuls/:simulId/edit",
      name: "simulEdit",
      component: SimulCreateOrEdit,
    },
    {
      path: "/simuls/:simulId",
      name: "simul",
      component: Simul,
    },
    {
      path: "/simuls/:simulId/games",
      name: "simulGames",
      component: SimulGames,
    },
    {
      path: "/tournaments",
      name: "tournaments",
      component: Tournaments,
    },
    {
      path: "/tournaments/:tournamentId",
      name: "tournament",
      component: Tournament,
    },
    {
      path: "/tournaments/:tournamentId/games/:gameId",
      name: "tournamentGame",
      component: Game,
    },
    {
      path: "/tournaments/:tournamentId/games/v2/:gameId",
      name: "tournamentGameV2",
      component: GameV2,
    },
    {
      path: "/tournaments/:tournamentId/live",
      name: "tournamentLive",
      component: TournamentLive,
    },
    {
      path: "/tournaments/:tournamentId/rounds/:roundNumber",
      name: "tournamentRound",
      component: TournamentRound,
    },
    {
      path: "/hall-of-fame",
      name: "HallOfFame",
      component: HallOfFame,
    },

    {
      path: "/leaderboards",
      name: "Leaderboards",
      component: Leaderboards,
    },

    {
      path: "/ratings",
      name: "Ratings",
      component: Ratings,
    },

    {
      path: "/lessons",
      name: "Lessons",
      component: Lessons,
    },

    {
      path: "/live-lessons",
      name: "LiveLessons",
      component: LiveLessons,
    },

    {
      path: "/admin/tournaments",
      name: "AdminTournaments",
      component: AdminTournaments,
    },

    {
      path: "/admin/tournaments/:tournamentId",
      name: "AdminTournament",
      component: AdminTournament,
    },

    {
      path: "/admin/tournament/simulation",
      name: "AdminTournamentSimulation",
      component: AdminTournamentSimulation,
    },

    {
      path: "/admin/tickets",
      name: "AdminTickets",
      component: AdminTickets,
    },

    {
      path: "/admin/news",
      name: "AdminNews",
      component: AdminNews,
    },

    {
      path: "/admin/notifications",
      name: "AdminNotifications",
      component: AdminNotifications,
    },

    {
      path: "/admin/permissions",
      name: "AdminPermissions",
      component: AdminPermissions,
    },

    {
      path: "/admin/sac-logs",
      name: "AdminSACLogs",
      component: AdminSACLogs,
    },

    {
      path: "/topics",
      name: "Topics",
      component: TopicExplorer,
      // meta: {
      //   teacher: true,
      //   student: true,
      // },
    },
    {
      path: "/topics/:topicId",
      name: "Topic",
      component: () =>
        import("./components/training/explorer/templates/TopicPage.vue"),
      // meta: {
      //   teacher: true,
      // },
    },
    {
      path: "/examples",
      name: "Examples",
      component: Coaching,
      // meta: {
      //   teacher: true,
      //   student: true,
      // },
    },

    {
      path: "/admin/curriculum",
      name: "AdminCurriculum",
      component: Curriculum,
      // meta: {
      //   teacher: true,
      //   student: true,
      // },
    },

    {
      path: "/lectures",
      name: "LecturesIndex",
      component: LecturesIndex,
      // meta: {
      //   teacher: true,
      //   student: true,
      // },
    },
    {
      path: "/lectures/:lectureId",
      name: "lecture",
      component: Lecture,
      // meta: {
      //   teacher: true,
      //   student: true,
      // },
      props: true,
    },

    {
      path: "/teacher/login",
      redirect: "/login",
    },

    {
      path: "/teacher",
      redirect: "/teacher/home",
    },

    {
      path: "/teacher/home",
      name: "TeacherHome",
      component: TeacherHome,
      meta: {
        student: false,
        teacher: true,
      },
    },

    {
      path: "/teacher/homework",
      name: "TeacherHomework",
      component: TeacherHomework,
      meta: {
        student: false,
        teacher: true,
      },
    },

    {
      path: "/teacher/progress",
      name: "TeacherStudentProgress",
      component: TeacherStudentProgress,
      meta: {
        student: false,
        teacher: true,
      },
    },

    {
      path: "/teacher/class-tournaments",
      name: "OfflineClassTournaments",
      component: OfflineClassTournaments,
      meta: {
        student: false,
        teacher: true,
      },
    },

    {
      path: "/teacher/class-tournaments/:id",
      name: "OfflineClassTournament",
      component: OfflineClassTournament,
      meta: {
        student: false,
        teacher: true,
      },
    },

    {
      path: "/teacher/class-tournaments/:id/presentation",
      name: "TournamentRoundPresentation",
      component: TournamentRoundPresentation,
      meta: {
        student: false,
        teacher: true,
      },
    },

    {
      path: "/warmup/:id/presentation",
      name: "WarmupRoundPresentation",
      component: WarmupRoundPresentation,
      meta: {
        student: false,
        teacher: true,
      },
    },

    {
      path: "/teacher/topics",
      name: "TeacherTopics",
      component: TopicExplorer,
      meta: {
        teacher: true,
        student: false,
      },
    },
    {
      path: "/teacher/topics/:topicId",
      name: "TeacherTopic",
      component: () =>
        import("./components/training/explorer/templates/TopicPage.vue"),
      meta: {
        teacher: true,
        student: false,
      },
    },
    {
      path: "/teacher/examples",
      name: "TeacherExamples",
      component: Coaching,
      meta: {
        teacher: true,
        student: false,
      },
    },

    {
      path: "/teacher/curriculum",
      name: "TeacherCurriculum",
      component: Curriculum,
      meta: {
        teacher: true,
        student: false,
      },
    },

    {
      path: "/teacher/lectures",
      name: "TeacherLecturesIndex",
      component: LecturesIndex,
      meta: {
        teacher: true,
        student: false,
      },
    },
    {
      path: "/teacher/lectures/calendar",
      name: "TeacherLecturesCalendar",
      component: LecturesCalendar,
      meta: {
        teacher: true,
        student: false,
      },
    },
    {
      path: "/teacher/lectures/:lectureId",
      name: "TeacherLecture",
      component: Lecture,
      meta: {
        teacher: true,
        student: false,
      },
      props: true,
    },

    {
      path: "/teacher/lessons",
      name: "TeacherLessons",
      component: Lessons,
      meta: {
        teacher: true,
        student: false,
      },
    },

    {
      path: "/teacher/liveLessons",
      name: "TeacherLiveLessons",
      component: LiveLessons,
      meta: {
        teacher: true,
        student: false,
      },
    },

    {
      path: "/test",
      name: "test",
      component: TestPage,
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  const skipAuth = to.meta?.skipAuth ?? false;

  if (skipAuth) {
    next();
    return;
  }

  const isTeacher = to.meta?.teacher ?? false;

  if (isTeacher) {
    const teacherAuth = updateSessionFromQuery(
      to,
      "teacherAuth",
      "teacherAuth"
    );

    // user might also be logging in as teacher via teacherAuth param in URL
    // update teacherAuth from route for teacher account
    // this takes next priority
    if (teacherAuth) {
      // teacherAuth from URl exists
      // remove existing teacher auth to avoid conflict
      localStorage.removeItem("accessToken");
    }

    const isAuthenticated = sacService.isAuthenticated();
    if (!isAuthenticated) {
      next({
        name: "login",
        query: { redirect: to.fullPath },
      });
      return;
    }

    // Clear student profile when accessing teacher routes
    store.commit("clearProfile");

    next();
    return;
  }

  // update accessToken from route
  if (updateSessionFromQuery(to, "token", "accessToken")) {
    // We have a student token in the URL, so we're switching to student mode
    let userId = await TokenHelper.getPayloadField("id");
    if (userId) {
      localStorage.setItem("userId", userId);

      // Clear teacher profile when accessing student routes
      store.commit("clearTeacherProfile");

      store.dispatch("queryProfileAction");
    }
  } else if (!store.getters.getUserId) {
    let userId = await TokenHelper.getPayloadField("id");
    if (userId) {
      localStorage.setItem("userId", userId);
      store.dispatch("queryProfileAction");
    }
  }

  // login route
  if (to.name === "login" && from.name !== "login") {
    next();
    return;
  }

  const tokenExpired = await TokenHelper.checkExpiry(null, to.fullPath);
  if (tokenExpired) {
    // Token is expired and auto sign-in failed, redirect to login
    next({
      name: "login",
      query: { redirect: to.fullPath },
    });
    return;
  }

  // Token is valid (either was already valid or auto sign-in succeeded)
  // Handle special case where user is going to login page but is already authenticated
  if (to.name === "login" && !tokenExpired) {
    next({ name: "home" });
    return;
  }

  next();
});

router.onError((error) => {
  // After a redeploy, a long-lived tab can lazily request an old chunk file
  // that no longer exists on hosting. Reload to pick up the new build, at
  // most once per minute so an offline tab doesn't reload-loop.
  if (/loading( css)? chunk/i.test(error.message)) {
    // A chunk can also fail to load because the connection is down — school
    // Wi-Fi often stays "connected" (navigator.onLine === true) with a dead
    // uplink. Reloading then would drop the coach on a browser error page
    // mid-lesson, since there is no service worker to serve the app shell.
    // Only reload once the server is confirmed reachable; otherwise stay on
    // the current route so the running lecture keeps working.
    if (navigator.onLine === false) return;

    const lastReload = Number(sessionStorage.getItem("chunkErrorReload") || 0);
    if (Date.now() - lastReload <= 60000) return;
    sessionStorage.setItem("chunkErrorReload", String(Date.now()));

    fetch("/", { method: "HEAD", cache: "no-store" })
      .then((res) => {
        if (res.ok) window.location.reload();
      })
      .catch(() => {});
  }
});

// Chunks a coach can need mid-lesson without having navigated to them first:
// LectureControl pushes to the class-tournament pages, and the warmup /
// tournament presentations open in a NEW window. If the room's Wi-Fi has
// dropped by then, an unfetched chunk can't load, so fetch these into the
// HTTP cache in the background once a teacher lands on any /teacher route.
// (This is deliberate, narrow warming — the blanket <link rel="prefetch">
// of every chunk is disabled in vue.config.js because it floods slow links.)
let teachingChunksWarmed = false;
router.afterEach((to) => {
  if (teachingChunksWarmed || !to.path.startsWith("/teacher")) return;
  teachingChunksWarmed = true;
  const warm = () => {
    [
      OfflineClassTournaments,
      OfflineClassTournament,
      TournamentRoundPresentation,
      WarmupRoundPresentation,
    ].forEach((load) => load().catch(() => {}));
  };
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(warm, { timeout: 10000 });
  } else {
    setTimeout(warm, 3000);
  }
});

router.afterEach((to) => {
  let query = Object.assign({}, to.query);
  if (query.token) {
    delete query.token;
    router.replace({ query });
  }
  document.title = "Chess Squad Academy";
});

const updateSessionFromQuery = (route, queryKey, sessionKey) => {
  if (sessionKey == null) sessionKey = queryKey;
  let value = route.query[queryKey];
  if (!value) return null;

  localStorage.setItem(sessionKey, value);

  const fullUrl = new URL(window.location.href);

  // 1. Get the part after the '?' inside the hash
  const hashParts = fullUrl.hash.split("?");
  if (hashParts.length > 1) {
    const hashPath = hashParts[0]; // "#/home"
    const hashParams = new URLSearchParams(hashParts[1]); // "user=123&token=abc"

    // 2. Delete the parameter
    hashParams.delete(queryKey);

    // 3. Reconstruct the hash and update the URL
    const newHash = hashParams.toString()
      ? `${hashPath}?${hashParams.toString()}`
      : hashPath;

    console.log(newHash);

    // Update the browser URL without a page reload
    window.history.replaceState(null, null, newHash);
    window.location.reload();
  }

  return value;
};

export default router;
